<template>
  <div v-if="authStore.isImpersonating" class="imp-banner" :class="{ 'imp-banner--demo': authStore.isDemo }" role="alert">
    <div class="imp-banner-inner">
      <div class="imp-banner-icon-wrap">
        <Presentation v-if="authStore.isDemo" :size="18" />
        <UserCog v-else :size="18" />
      </div>

      <!-- Demo-Modus (internal/demo): Vorführung unter einer Demo-Rolle. -->
      <div v-if="authStore.isDemo" class="imp-banner-text">
        <span class="imp-banner-title">{{ $gt('Demo-Modus') }}</span>
        <span class="imp-banner-desc">
          {{ $gt('Ansicht als') }} <strong>{{ authStore.user?.organizationName }}</strong>{{ $gt('. Es werden keine E-Mails versendet – weder an Kunden noch an Beteiligte.') }}
        </span>
      </div>
      <div v-else class="imp-banner-text">
        <span class="imp-banner-title">{{ $gt('SuperAdmin-Sitzung') }}</span>
        <span class="imp-banner-desc">
          {{ $gt('Sie sehen die Plattform aus Sicht von') }}
          <strong>{{ targetLabel }}</strong>
          <span v-if="authStore.user?.userRole" class="imp-banner-role">({{ authStore.user.userRole }})</span>{{ $gt('. Aktionen werden diesem Account zugerechnet.') }}
        </span>
      </div>

      <button class="imp-banner-stop-btn" :disabled="stopping" @click="handleStop">
        <LogOut :size="14" />
        <span>{{ stopping ? "Beende..." : authStore.isDemo ? "Demo beenden" : "Sitzung beenden" }}</span>
      </button>
    </div>
  </div>
</template>

<script>
import { LogOut, Presentation, UserCog } from "lucide-vue-next";
import { useAuthStore } from "@/stores/authStore";

export default {
  name: "ImpersonationBanner",

  components: { LogOut, Presentation, UserCog },

  setup() {
    return { authStore: useAuthStore() };
  },

  data() {
    return { stopping: false };
  },

  computed: {
    targetLabel() {
      const u = this.authStore.user;
      if (!u) return "";
      const fullName = [u.firstName, u.lastName].filter(Boolean).join(" ");
      if (fullName) return `${fullName} (${u.email})`;
      return u.email;
    },
  },

  methods: {
    async handleStop() {
      if (this.stopping) return;
      this.stopping = true;
      try {
        await this.authStore.stopImpersonating();
      } finally {
        this.stopping = false;
      }
    },
  },
};
</script>

<style scoped>
.imp-banner {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 9999;
  background: linear-gradient(90deg, #b45309 0%, #d97706 100%);
  color: #fff;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.18);
  font-size: 0.825rem;
  line-height: 1.35;
  min-height: 44px;
}

.imp-banner--demo {
  background: linear-gradient(90deg, #047857 0%, #059669 100%);
}

.imp-banner-inner {
  max-width: 1400px;
  margin: 0 auto;
  padding: 6px 18px;
  display: flex;
  align-items: center;
  gap: 12px;
}

.imp-banner-icon-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  background: rgba(255, 255, 255, 0.18);
  border-radius: 8px;
  flex-shrink: 0;
}

.imp-banner-text {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
}

.imp-banner-title {
  font-weight: 600;
  letter-spacing: 0.02em;
  font-size: 0.7rem;
  text-transform: uppercase;
  opacity: 0.9;
}

.imp-banner-desc {
  font-size: 0.825rem;
  white-space: normal;
}

.imp-banner-role {
  margin-left: 4px;
  opacity: 0.85;
}

.imp-banner-stop-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(255, 255, 255, 0.18);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.45);
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s ease;
  white-space: nowrap;
  flex-shrink: 0;
}

.imp-banner-stop-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.32);
}

.imp-banner-stop-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

@media (max-width: 640px) {
  .imp-banner-inner {
    flex-wrap: wrap;
    padding: 8px 12px;
  }
  .imp-banner-text {
    flex: 1 1 100%;
    order: 2;
  }
  .imp-banner-stop-btn {
    order: 3;
    margin-left: auto;
  }
}
</style>
